import * as Sentry from '@sentry/react';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { stringify } from 'qs';
import { isServerError } from 'src/hooks/use-axios/errors';
import { Cognito } from 'src/hooks/use-cognito';
import { safeLocalStorage } from 'src/hooks/use-storage';
import { addBreadcrumb } from 'src/utils/breadcrumbs';
import { isTokenAuthenticationMissingClaims } from 'src/utils/is-token-authenication-missing-claims';
import { normalizePayload } from 'src/utils/normalize-payload';

export const paramsSerializer = (params: unknown) =>
  stringify(params, { arrayFormat: 'repeat', skipNulls: true });

declare module 'axios' {
  // 1) we need to extend the axios config interface in order to add an optional
  //    custom field, which will be used in our interceptors
  // 2) passing a custom field to axios is generally allowed
  //    see https://github.com/axios/axios/pull/2207
  export interface AxiosRequestConfig {
    publicFallback?: string;
  }
}

const jwtKey = 'JWT';
const jwtUrl = `${PRISMA_CONFIG.oldBackendUrl}/auth/jwt`;
type JwtResult = { jwt: string };

let savedJwt = safeLocalStorage.getItem(jwtKey);

export function removeStoredMonolithJwt() {
  savedJwt = null;
  safeLocalStorage.removeItem(jwtKey);
}

// simple cache to deduplicate requests
let tokenPromise: Promise<AxiosResponse<JwtResult>> | null = null;

export async function fetchMonolithJwt(axios: AxiosInstance) {
  let tokenResponse: AxiosResponse<JwtResult> | null = null;

  if (!tokenPromise) {
    tokenPromise = axios.request<JwtResult>({
      url: jwtUrl,
      // if the old platform is too slow to respond (>= 10s) a timeout
      // error will be thrown - that way we default to unauthenticated behavior
      // to get at least a working app for every user who wouldn't want to
      // authenticate anyway (and users who want to authenticate would need
      // to use the login)
      timeout: 10000,
    });
  }
  tokenResponse = await tokenPromise;
  tokenPromise = null;

  const jwt = tokenResponse.data.jwt;
  savedJwt = jwt;
  safeLocalStorage.setItem(jwtKey, jwt);
}

function isNewApi(url: string | undefined) {
  return (
    url?.startsWith(PRISMA_CONFIG.organisationRegistration) ||
    url?.startsWith(PRISMA_CONFIG.shipperRegistration) ||
    url?.startsWith(PRISMA_CONFIG.organisationService) ||
    url?.startsWith(PRISMA_CONFIG.cxMeasurement) ||
    url?.startsWith(PRISMA_CONFIG.iipService) ||
    url?.startsWith(PRISMA_CONFIG.capacityManagement) ||
    url?.startsWith(PRISMA_CONFIG.contractManagement) ||
    url?.startsWith(PRISMA_CONFIG.documentExchange) ||
    url?.startsWith(PRISMA_CONFIG.surrenderService) ||
    url?.startsWith(PRISMA_CONFIG.gasDataManagement) ||
    url?.startsWith(PRISMA_CONFIG.transparencyReporting) ||
    (PRISMA_CONFIG.directFcfs != null &&
      url?.startsWith(PRISMA_CONFIG.directFcfs)) ||
    (PRISMA_CONFIG.remitReporting != null &&
      url?.startsWith(PRISMA_CONFIG.remitReporting)) ||
    url?.startsWith(PRISMA_CONFIG.aggregateEu) ||
    (PRISMA_CONFIG.authorizationService != null &&
      url?.startsWith(PRISMA_CONFIG.authorizationService)) ||
    url?.startsWith(PRISMA_CONFIG.snamBidCollector) ||
    url?.startsWith(PRISMA_CONFIG.networkPoints)
  );
}

const usesCognitoTokenConfig: Record<string, (string | null)[]> = {
  local: [
    PRISMA_CONFIG.oldBackendUrl,
    PRISMA_CONFIG.iipService,
    PRISMA_CONFIG.organisationService,
    PRISMA_CONFIG.shipperRegistration,
    PRISMA_CONFIG.contractManagement,
    PRISMA_CONFIG.documentExchange,
    PRISMA_CONFIG.aggregateEu,
    PRISMA_CONFIG.capacityManagement,
    PRISMA_CONFIG.surrenderService,
    PRISMA_CONFIG.networkPoints,
    PRISMA_CONFIG.snamBidCollector,
    PRISMA_CONFIG.gasDataManagement,
    PRISMA_CONFIG.transparencyReporting,
    PRISMA_CONFIG.remitReporting,
    PRISMA_CONFIG.authorizationService,
    PRISMA_CONFIG.directFcfs,
  ],
  storybook: [
    PRISMA_CONFIG.oldBackendUrl,
    PRISMA_CONFIG.iipService,
    PRISMA_CONFIG.organisationService,
    PRISMA_CONFIG.shipperRegistration,
    PRISMA_CONFIG.contractManagement,
    PRISMA_CONFIG.documentExchange,
    PRISMA_CONFIG.aggregateEu,
    PRISMA_CONFIG.capacityManagement,
    PRISMA_CONFIG.surrenderService,
    PRISMA_CONFIG.networkPoints,
    PRISMA_CONFIG.snamBidCollector,
    PRISMA_CONFIG.gasDataManagement,
    PRISMA_CONFIG.transparencyReporting,
    PRISMA_CONFIG.remitReporting,
    PRISMA_CONFIG.authorizationService,
    PRISMA_CONFIG.directFcfs,
  ],
  ent: [
    PRISMA_CONFIG.oldBackendUrl,
    PRISMA_CONFIG.iipService,
    PRISMA_CONFIG.organisationService,
    PRISMA_CONFIG.shipperRegistration,
    PRISMA_CONFIG.contractManagement,
    PRISMA_CONFIG.documentExchange,
    PRISMA_CONFIG.aggregateEu,
    PRISMA_CONFIG.capacityManagement,
    PRISMA_CONFIG.surrenderService,
    PRISMA_CONFIG.networkPoints,
    PRISMA_CONFIG.snamBidCollector,
    PRISMA_CONFIG.gasDataManagement,
    PRISMA_CONFIG.transparencyReporting,
    PRISMA_CONFIG.remitReporting,
    PRISMA_CONFIG.authorizationService,
    PRISMA_CONFIG.directFcfs,
  ],
  int: [
    PRISMA_CONFIG.oldBackendUrl,
    PRISMA_CONFIG.iipService,
    PRISMA_CONFIG.organisationService,
    PRISMA_CONFIG.shipperRegistration,
    PRISMA_CONFIG.contractManagement,
    PRISMA_CONFIG.documentExchange,
    PRISMA_CONFIG.aggregateEu,
    PRISMA_CONFIG.capacityManagement,
    PRISMA_CONFIG.surrenderService,
    PRISMA_CONFIG.networkPoints,
    PRISMA_CONFIG.snamBidCollector,
    PRISMA_CONFIG.gasDataManagement,
    PRISMA_CONFIG.transparencyReporting,
    PRISMA_CONFIG.remitReporting,
    PRISMA_CONFIG.authorizationService,
    PRISMA_CONFIG.directFcfs,
  ],
  test: [
    PRISMA_CONFIG.oldBackendUrl,
    PRISMA_CONFIG.iipService,
    PRISMA_CONFIG.organisationService,
    PRISMA_CONFIG.shipperRegistration,
    PRISMA_CONFIG.contractManagement,
    PRISMA_CONFIG.documentExchange,
    PRISMA_CONFIG.aggregateEu,
    PRISMA_CONFIG.capacityManagement,
    PRISMA_CONFIG.surrenderService,
    PRISMA_CONFIG.networkPoints,
    PRISMA_CONFIG.snamBidCollector,
    PRISMA_CONFIG.gasDataManagement,
    PRISMA_CONFIG.transparencyReporting,
    PRISMA_CONFIG.remitReporting,
    PRISMA_CONFIG.authorizationService,
    PRISMA_CONFIG.directFcfs,
  ],
  kon: [
    // PRISMA_CONFIG.oldBackendUrl,
    PRISMA_CONFIG.organisationService,
    PRISMA_CONFIG.shipperRegistration,
    PRISMA_CONFIG.contractManagement,
    PRISMA_CONFIG.documentExchange,
    PRISMA_CONFIG.aggregateEu,
    PRISMA_CONFIG.gasDataManagement,
    PRISMA_CONFIG.transparencyReporting,
    PRISMA_CONFIG.remitReporting,
    PRISMA_CONFIG.authorizationService,
    PRISMA_CONFIG.directFcfs,
  ],
  prod: [
    PRISMA_CONFIG.oldBackendUrl,
    PRISMA_CONFIG.iipService,
    PRISMA_CONFIG.organisationService,
    PRISMA_CONFIG.shipperRegistration,
    PRISMA_CONFIG.contractManagement,
    PRISMA_CONFIG.documentExchange,
    PRISMA_CONFIG.aggregateEu,
    PRISMA_CONFIG.capacityManagement,
    PRISMA_CONFIG.surrenderService,
    PRISMA_CONFIG.networkPoints,
    PRISMA_CONFIG.snamBidCollector,
    PRISMA_CONFIG.gasDataManagement,
    PRISMA_CONFIG.transparencyReporting,
    PRISMA_CONFIG.remitReporting,
    PRISMA_CONFIG.authorizationService,
    PRISMA_CONFIG.directFcfs,
  ],
};

const usesCognitoToken = usesCognitoTokenConfig[PRISMA_CONFIG.stage];
if (!usesCognitoToken)
  throw `Missing stage "${PRISMA_CONFIG.stage}" in usesCognitoTokenConfig.`;
export { usesCognitoToken };

// see https://prisma.atlassian.net/browse/FOO-988
const StateTokenHeader = 'x-prisma-iip-state-token';
let stateTokenForIip: string | null = null;

// either get cognito access token or old platform jwt
async function getToken(cognito: Cognito, config: AxiosRequestConfig) {
  if (
    usesCognitoToken.some(
      (service) => service && config.url?.startsWith(service)
    )
  ) {
    const user = cognito.getCurrentUser();
    if (!user) return null;
    try {
      const session = await cognito.getSession(user);
      return session.getIdToken().getJwtToken();
    } catch (err) {
      return null;
    }
  } else {
    savedJwt = safeLocalStorage.getItem(jwtKey);
    return savedJwt;
  }
}

export async function refreshCognitoToken(cognito: Cognito) {
  const user = cognito.getCurrentUser();
  if (!user) return updateSentryTracking(null);
  try {
    let session = await cognito.getSession(user);
    if (!session.isValid()) {
      await cognito.refreshSession(user, session.getRefreshToken());
      session = await cognito.getSession(user);
    }
    return updateSentryTracking(session.getIdToken().getJwtToken());
  } catch (err) {
    try {
      await cognito.logout();
    } catch (err) {
      // ignore
    }
    return updateSentryTracking(null);
  }
}

// track user id in sentry
function updateSentryTracking(token: string | null) {
  try {
    const parsedToken = JSON.parse(atob(token!.split('.')[1]));
    const userId = parsedToken.user_id;
    Sentry.setExtra('userId', userId);
  } catch (e) {
    Sentry.setExtra('userId', null);
  }
  return token;
}

// either refresh cognito access token or old platform jwt
async function refreshToken(
  cognito: Cognito,
  axiosInstance: AxiosInstance,
  config: AxiosRequestConfig,
  forceMonolith: boolean
) {
  if (
    !forceMonolith &&
    usesCognitoToken.some(
      (service) => service && config.url?.startsWith(service)
    )
  ) {
    return refreshCognitoToken(cognito);
  } else {
    await fetchMonolithJwt(axiosInstance);
    return savedJwt;
  }
}

export function createAxiosInstance({
  cognito,
  safeLocalStorage,
}: {
  cognito: Cognito;
  safeLocalStorage: Storage;
}) {
  const axiosInstance = axios.create({
    paramsSerializer: { serialize: paramsSerializer },
  });

  axiosInstance.interceptors.request.use(async (config) => {
    if (config.url?.startsWith(PRISMA_CONFIG.oldBackendUrl)) {
      // needed, because of different monolith nodes to stay sticky
      config.withCredentials = true;
    }

    // if token is available, we add it automatically to new api url requests
    const token = await getToken(cognito, config);

    if (
      (isNewApi(config.url) ||
        config.url?.startsWith(PRISMA_CONFIG.oldBackendUrl)) &&
      token
    ) {
      // keep existing Authorization header if present (e.g. our retry logic might
      // added an Authorization header already)
      config.headers.Authorization =
        config.headers.Authorization ?? `Bearer ${token}`;

      if (
        config.url?.startsWith(PRISMA_CONFIG.oldBackendUrl) &&
        safeLocalStorage.getItem('ENFORCE_SHIPPER_ROLE') === 'true'
      )
        config.headers['X-Prisma-Switch-User'] = 'true';
    }

    // if token is not available, but a public fallback is, we automatically switch to the fallback
    if (isNewApi(config.url) && !token && config.publicFallback) {
      config.url = config.publicFallback;
      delete config.publicFallback;
    }

    if (isNewApi(config.url)) {
      config.data = normalizePayload(config.data);
    }

    if (config.url?.startsWith(PRISMA_CONFIG.iipService) && stateTokenForIip) {
      config.headers[StateTokenHeader] = stateTokenForIip;
    }

    return config;
  });

  axiosInstance.interceptors.response.use((value) => {
    addBreadcrumb({
      type: 'http',
      response: {
        method: value.config.method,
        url: value.config.url,
        status: value.status,
        statusText: value.statusText,
        traceId: value.headers['x-amzn-trace-id'],
      },
      frontend: 'react',
    });

    if (
      value.config.url?.startsWith(PRISMA_CONFIG.iipService) &&
      value.headers[StateTokenHeader]
    ) {
      stateTokenForIip = value.headers[StateTokenHeader];
    }
    return value;
  });

  const WithRefreshedTokenHeader = 'X-Axios-Interceptor-Jwt-Refresh';

  axiosInstance.interceptors.response.use(undefined, async (error) => {
    // handle 401s and retry requests if possible
    if (
      isServerError(error, 401) &&
      (isNewApi(error.config.url) ||
        (PRISMA_CONFIG.stage !== 'kon' &&
          error.config.url?.startsWith(PRISMA_CONFIG.oldBackendUrl))) &&
      error.config.headers[WithRefreshedTokenHeader] !== 'true'
    ) {
      // try to refresh token once
      try {
        const token = await refreshToken(
          cognito,
          axiosInstance,
          error.config,
          isTokenAuthenticationMissingClaims(error)
        );
        if (!token) throw error;

        return axiosInstance.request({
          ...error.config,
          headers: {
            ...error.config.headers,
            Authorization: `Bearer ${token}`,
            [WithRefreshedTokenHeader]: 'true',
          },
        });
      } catch (tokenRefreshError) {
        removeStoredMonolithJwt();
        // if any error happened during refreshing the token, we try to use a public fallback
        if (error.config.publicFallback) {
          return axiosInstance.request({
            ...error.config,
            url: error.config.publicFallback,
            publicFallback: undefined,
          });
        } else {
          throw error;
        }
      }
    } else {
      throw error;
    }
  });

  return axiosInstance;
}
